import React, { Fragment, useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Button,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Eye, Edit, Power, Trash2 } from "react-feather";

import { Breadcrumbs, H5 } from "../../AbstractElements";
import DataTables from "../../Components/Tables/DataTable";
import { titleCase } from "../../utils/case";
import { deletePromotion, getPromotions, updatePromotion } from "../../api/promotion.api";
import { hasRole } from "../../utils/auth";

const PromotionMain = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canManagePromotions = hasRole("superadmin", "finance");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    perPage: 10,
    totalPages: 1,
  });

  const [sortConfig, setSortConfig] = useState({
    sortBy: "createdAt",
    sortOrder: "DESC",
  });

  const fetchPromotions = async (
    page = 1,
    perPage = 10,
    sortBy = sortConfig.sortBy,
    sortOrder = sortConfig.sortOrder
  ) => {
    try {
      setLoading(true);

      const res = await getPromotions({
        page,
        perPage,
        sortField: sortBy,
        sortOrder,
      });

      setData(res || []);
      setMeta(
        res?.meta || {
          total: 0,
          page,
          perPage,
          totalPages: 1,
        }
      );
    } catch (err) {
      toast.error(err.message || "failed to fetch promotions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromotions(meta.page, meta.perPage);
  }, []);

  const handlePageChange = (newPage) => {
    fetchPromotions(
      newPage,
      meta.perPage,
      sortConfig.sortBy,
      sortConfig.sortOrder
    );
  };

  const handlePerPageChange = (newPerPage) => {
    fetchPromotions(
      1,
      newPerPage,
      sortConfig.sortBy,
      sortConfig.sortOrder
    );
  };

  const handleSort = (column, direction) => {
    const sortBy = column.sortField || "createdAt";
    const sortOrder = direction === "asc" ? "ASC" : "DESC";

    setSortConfig({
      sortBy,
      sortOrder,
    });

    fetchPromotions(meta.page, meta.perPage, sortBy, sortOrder);
  };

  const getPromotionType = (row) => {
    if (row.discountType) {
      return row.discountType;
    }

    if (row.type) {
      return row.type;
    }

    return "-";
  };

  const getDiscountValue = (row) => {
    if (row.discountType === "percentage") {
      return `${row.discountValue || 0}%`;
    }

    if (row.discountType === "fixed") {
      return `RM ${Number(row.discountValue || 0).toFixed(2)}`;
    }

    if (row.amount !== undefined && row.amount !== null) {
      return `RM ${Number(row.amount).toFixed(2)}`;
    }

    return "-";
  };

  const columns = [
    {
      name: titleCase(t("code")),
      selector: (row) => row.promoCode || "-",
      sortable: true,
      sortField: "code",
    },
    {
      name: titleCase(t("Usage Count")),
      selector: (row) => row.usageCount || 0,
      sortable: true,
      sortField: "usageCount",
    },
    {
      name: titleCase(t("Total Discounted (RM)")),
      selector: (row) => row.totalDiscounted || 0,
      sortable: true,
      sortField: "totalDiscounted",
    },
    {
      name: titleCase(t("start date")),
      selector: (row) => row.startDate || "-",
      sortable: true,
      sortField: "startDate",
    },
    {
      name: titleCase(t("end date")),
      selector: (row) => row.endDate || "Unlimited",
      sortable: true,
      sortField: "endDate",
    },
    {
      name: titleCase(t("status")),
      selector: (row) => row.status,
      center: true,
      sortable: true,
      sortField: "status",
      cell: (row) => (
        <div style={{ width: "100%", textAlign: "center" }}>
          <Badge color={row.status === "active" ? "success" : "dark"}>
            {titleCase(t(row.status || "inactive"))}
          </Badge>
        </div>
      ),
    },
    {
      name: titleCase(t("action")),
      cell: (row) => (
        <div className="d-flex gap-2 align-items-center">
          <Eye
            size={18}
            style={{
              cursor: "pointer",
              color: "#7da8c7",
            }}
            title={titleCase(t("view"))}
            onClick={() => navigate(`/promotions/view/${row.id}`)}
          />

          {canManagePromotions && (
            <>
              <Edit
                size={18}
                style={{
                  cursor: "pointer",
                  color: "#d4a437",
                }}
                title={titleCase(t("edit"))}
                onClick={() => navigate(`/promotions/edit/${row.id}`)}
              />
              <Power
                size={18}
                color={row.status === "active" ? "#ff9800" : "#28a745"}
                style={{ cursor: "pointer" }}
                title={titleCase(
                  t(row.status === "active" ? "deactivate" : "activate")
                )}
                onClick={() => handleToggleStatus(row)}
              />

              <Trash2
                size={18}
                color="#dc3545"
                style={{ cursor: "pointer" }}
                title={titleCase(t("delete"))}
                onClick={() => handleDelete(row)}
              />
            </>
          )}
        </div>
      ),
    },
  ];

  const handleToggleStatus = async (promotion) => {
    const nextStatus =
      promotion.status === "active"
        ? "inactive"
        : "active";

    if (
      !window.confirm(
        nextStatus === "inactive"
          ? t("Are you sure you want to deactivate this promotion?")
          : t("Are you sure you want to activate this promotion?")
      )
    ) {
      return;
    }

    try {
      await updatePromotion(promotion.id, {
        status: nextStatus,
      });

      toast.success(
        nextStatus === "inactive"
          ? t("Promotion deactivated successfully.")
          : t("Promotion activated successfully.")
      );

      fetchPromotions(
        meta.page,
        meta.perPage,
        sortConfig.sortBy,
        sortConfig.sortOrder
      );
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (promotion) => {
    if (
      !window.confirm(
        t("Are you sure you want to delete this promotion?")
      )
    ) {
      return;
    }

    try {
      await deletePromotion(promotion.id);

      toast.success(t("Deleted successfully."));

      fetchPromotions(
        meta.page,
        meta.perPage,
        sortConfig.sortBy,
        sortConfig.sortOrder
      );
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("promotions"))}
        title={titleCase(t("promotions management"))}
        mainTitle={titleCase(t("promotions management"))}
      />

      <Container fluid>
        <Row>
          <Col md="12">
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between">
                <H5>{titleCase(t("promotions list"))}</H5>

                {canManagePromotions && (
                  <Button
                    color="success"
                    onClick={() => navigate("/promotions/create")}
                  >
                    {t("create").toUpperCase()}
                  </Button>
                )}
              </CardHeader>

              <CardBody>
                <DataTables
                  initialData={data}
                  columns={columns}
                  loading={loading}
                  meta={meta}
                  onPageChange={handlePageChange}
                  onPerPageChange={handlePerPageChange}
                  onSort={handleSort}
                  sortServer
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default PromotionMain;